import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import AuthLayout from "@/components/auth/AuthLayout";
import TwoFactorCodeForm from "@/components/auth/TwoFactorCodeForm";
import { useAuth, homeForRole } from "@/context/AuthContext";

// Landing spot for the Google OAuth flow. The backend redirects here with our
// JWT in the URL fragment (#token=...&next=...). We establish the session and
// route on by role — never leaving the token sitting in the address bar.
//
// When the account has 2FA on, the fragment carries a short-lived challenge
// (#twofa=...&next=...) instead of a session token: Google proved the identity,
// so we still have to collect a TOTP code and exchange it via the same
// /auth/2fa/verify step the password login uses.
export default function AuthCallback() {
  const navigate = useNavigate();
  const { loginWithToken, verify2fa } = useAuth();
  const [failed, setFailed] = useState(false);
  // 2FA challenge state — set when the backend asks for a TOTP code.
  const [twofa, setTwofa] = useState(null); // { token, next } or null
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard against React 18 double-invoke in dev
    ran.current = true;

    const hash = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(hash);
    const token = params.get("token");
    const twofaToken = params.get("twofa");
    const nextRaw = params.get("next");
    // Only honour in-app paths (open-redirect guard mirrors the backend).
    const next =
      nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : null;

    if (twofaToken) {
      setTwofa({ token: twofaToken, next });
      // Unlike the completed sign-in below, this branch stays on the page while
      // the user types the code — strip the fragment so the challenge isn't left
      // sitting in the address bar.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      return;
    }

    if (!token) {
      navigate("/login?error=google", { replace: true });
      return;
    }

    (async () => {
      try {
        const { user } = await loginWithToken(token);
        navigate(next || homeForRole(user?.role || "tester"), { replace: true });
      } catch (e) {
        setFailed(true);
        setTimeout(() => navigate("/login?error=google", { replace: true }), 1500);
      }
    })();
  }, [loginWithToken, navigate]);

  // Accepts a TOTP code or a backup code. Errors propagate to the form, which
  // shows them and clears the input.
  const submit2fa = async (code) => {
    const result = await verify2fa(twofa.token, code);
    const role = result?.user?.role || "tester";
    navigate(twofa.next || homeForRole(role), { replace: true });
  };

  if (twofa) {
    return (
      <AuthLayout
        title="Two-factor authentication"
        subtitle="One more step to finish signing in."
      >
        <TwoFactorCodeForm
          onSubmit={submit2fa}
          onCancel={() => navigate("/login", { replace: true })}
        />
      </AuthLayout>
    );
  }

  return (
    // dark:bg-ink-950 — this was the one page using dark:bg-slate-950, which is a
    // visibly different (near-black) shade and flashed on the OAuth return.
    <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-600 dark:bg-ink-950 dark:text-slate-300">
      {failed ? (
        <p className="text-sm">Sign-in failed. Taking you back…</p>
      ) : (
        <p className="flex items-center gap-2 text-sm">
          <Loader2 size={18} className="animate-spin" /> Signing you in…
        </p>
      )}
    </div>
  );
}
